import { addMinutes, isAfter, isBefore, subDays, subMinutes } from "date-fns";

import { QueryClient } from "@formant/core/client/QueryClient";
import { stats } from "@formant/core/common/stats";
import { IQuery } from "@formant/core/model/IQuery";
import { IStreamData } from "@formant/core/model/IStreamData";

import { IAuthStore } from "./IAuthStore";
import { IOnDemandStore } from "./IOnDemandStore";
import { SegmentCache } from "./SegmentCache";

export class QuerySegmentCache extends SegmentCache<IQuery, IStreamData> {
    constructor(
        interval: "minute" | "hour",
        private queryClient: QueryClient,
        private authStore: IAuthStore,
        private onDemandStore: IOnDemandStore,
    ) {
        super(interval);
    }

    protected fetch(token: string, query: IQuery): Promise<IStreamData[]> {
        stats.increment("QuerySegmentCache_fetch");
        return this.queryClient.query(token, query);
    }

    protected mergeResults(allStreamDatas: IStreamData[][]): IStreamData[] {
        const results: { [id: string]: IStreamData } = {};

        allStreamDatas.forEach(streamDatas =>
            streamDatas.forEach(streamData => {
                const { deviceId, name, type, tags, points } = streamData;
                const key = JSON.stringify({ deviceId, name, type, tags });
                results[key] = results[key] || {
                    ...streamData,
                    points: [],
                };
                results[key].points = results[key].points.concat(points);
            }),
        );

        return Object.values(results);
    }

    protected segmentResult(
        start: Date,
        end: Date,
        streamData: IStreamData[],
    ): IStreamData[] {
        // The query API does not guarantee chronological point order (late
        // ingestion can interleave older datapoints), but consumers treat the
        // last point as the most recent one. Sort each segment so that merged
        // results are chronological end to end.
        return streamData
            .map(stream => ({
                ...stream,
                points: stream.points
                    .filter(
                        ([timestamp]) =>
                            timestamp >= start.getTime() &&
                            timestamp < end.getTime(),
                    )
                    .sort(([a], [b]) => a - b),
            }))
            .filter(({ points }) => points.length > 0);
    }

    protected isSegmentUpdating(segment: Date, query: IQuery): boolean {
        // if we are sharing or capturing, don't update segments
        if (this.authStore.shareCode) {
            return false;
        }
        if (this.segmentSize !== "minute") {
            // hour, day, month too large to hot reload
            return false;
        }
        const segmentStartTime = segment;
        const segmentEndTime = addMinutes(segmentStartTime, 1);

        // lets check if on-demand is updating this region actively
        for (const deviceId of query.deviceIds || []) {
            // lets first see if we even have any potential on-demand streams in the query quickly
            const onDemandStreams =
                this.onDemandStore.getOnDemandStreamNames(deviceId);
            if (onDemandStreams.size === 0) {
                // there's no on-demand streams so nothing need ever update faster than normal
                continue;
            }

            // check to see if the query constraints on our names, leaves
            // any on demand at all that could possibly need update
            if (query.names || query.notNames) {
                const onDemandNameSet = query.names
                    ? new Set(query.names.filter(_ => onDemandStreams.has(_)))
                    : onDemandStreams;

                const excludedNameSet = new Set(query.notNames || []);

                excludedNameSet.forEach(_ => onDemandNameSet.delete(_));

                if (onDemandNameSet.size === 0) {
                    continue;
                }
            }

            // let's only look at on-demand requests in the last day
            const oneDayAgo = subDays(new Date(), 1);
            const recentOnDemandRequests = this.onDemandStore
                .getRecentOnDemandRequests(
                    deviceId,
                    oneDayAgo.toISOString(),
                    new Date().toISOString(),
                )
                .filter(
                    _ => _.receivedTime && isAfter(_.receivedTime, oneDayAgo),
                );

            // if there's nothing
            if (recentOnDemandRequests.length === 0) {
                // no recent on-demand requests, don't update
                continue;
            }

            for (const request of recentOnDemandRequests) {
                if (request.finished && request.finishedTime) {
                    // lets see if a our command happened recently
                    // it might not have all its data ingested
                    const ingestionLikelyEndedTime = addMinutes(
                        request.finishedTime,
                        1,
                    );
                    const now = new Date();
                    // if we are 1 minutes after a command finished, lets keep those segments hot
                    if (
                        isAfter(now, request.finishedTime) &&
                        isBefore(now, ingestionLikelyEndedTime) &&
                        isAfter(segmentEndTime, request.start) &&
                        isBefore(segmentStartTime, request.end)
                    ) {
                        // found hot segment after on-demand finished
                        return true;
                    }
                } else if (
                    !request.finished &&
                    request.received === true &&
                    isAfter(segmentEndTime, request.start) &&
                    isBefore(segmentStartTime, request.end)
                ) {
                    if (!request.pending && request.progress > 0) {
                        // if we're sure its running, invalidate cache
                        return true;
                    } else {
                        // if we're less sure its running, let's see if it
                        // was received by device in last 15 minutes
                        const fifteenMinutesAgo = subMinutes(new Date(), 15);
                        if (
                            request.receivedTime &&
                            isAfter(request.receivedTime, fifteenMinutesAgo)
                        ) {
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    }
}
